import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Plus, Trash2, Save, Package, Search, X, GripVertical,
  ChevronDown, ChevronUp, Settings2, Percent, DollarSign,
  Utensils, Music, Camera, Users, Loader2, ArrowLeft, Eye
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  food: Utensils,
  drinks: Utensils,
  entertainment: Music,
  photography: Camera,
  staffing: Users,
};

const CATEGORY_COLORS = {
  food: 'bg-orange-500/10 text-orange-600 border-orange-200',
  drinks: 'bg-purple-500/10 text-purple-600 border-purple-200',
  entertainment: 'bg-pink-500/10 text-pink-600 border-pink-200',
  photography: 'bg-blue-500/10 text-blue-600 border-blue-200',
  staffing: 'bg-green-500/10 text-green-600 border-green-200',
};

const PRICING_LABELS = {
  per_person: '/person',
  per_hour: '/hr',
  flat_rate: ' flat',
  per_hour_per_person: '/hr/person',
};

export default function PackageBuilder() {
  const { token } = useAuth();
  const [packages, setPackages] = useState([]);
  const [allServices, setAllServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [showServicePicker, setShowServicePicker] = useState(false);
  const [serviceSearch, setServiceSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [previewPackage, setPreviewPackage] = useState(null);

  // Package form
  const [form, setForm] = useState({
    name: '',
    description: '',
    short_description: '',
    image_url: '',
    services: [],
    discount_type: 'none',
    discount_value: 0,
    min_guests: 20,
    max_guests: 200,
    is_active: true,
    is_featured: false,
    tags: [],
  });

  const fetchPackages = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/packages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPackages(res.data.packages || []);
    } catch (err) {
      console.error('Failed to fetch packages:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchServices = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/services/products?active_only=true`);
      setAllServices(res.data.products || []);
    } catch (err) {
      console.error('Failed to fetch services:', err);
    }
  }, []);

  useEffect(() => {
    fetchPackages();
    fetchServices();
  }, [fetchPackages, fetchServices]);

  const resetForm = () => {
    setEditingPackage(null);
    setShowBuilder(false);
    setForm({
      name: '', description: '', short_description: '', image_url: '',
      services: [], discount_type: 'none', discount_value: 0,
      min_guests: 20, max_guests: 200, is_active: true, is_featured: false, tags: [],
    });
  };

  const handleEdit = (pkg) => {
    setEditingPackage(pkg);
    setForm({
      name: pkg.name || '',
      description: pkg.description || '',
      short_description: pkg.short_description || '',
      image_url: pkg.image_url || '',
      services: pkg.services || [],
      discount_type: pkg.discount_type || 'none',
      discount_value: pkg.discount_value || 0,
      min_guests: pkg.min_guests || 20,
      max_guests: pkg.max_guests || 200,
      is_active: pkg.is_active ?? true,
      is_featured: pkg.is_featured ?? false,
      tags: pkg.tags || [],
    });
    setShowBuilder(true);
  };

  const handleDelete = async (packageId) => {
    if (!window.confirm('Delete this package?')) return;
    try {
      await axios.delete(`${API}/admin/packages/${packageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Package deleted');
      fetchPackages();
    } catch (err) {
      toast.error('Failed to delete package');
    }
  };

  const addService = (service) => {
    if (form.services.find(s => s.service_id === service.service_id)) {
      toast.error('Service already in package');
      return;
    }
    setForm(prev => ({
      ...prev,
      services: [...prev.services, {
        service_id: service.service_id,
        name: service.name,
        category: service.category,
        image_url: service.image_url || '',
        base_price: service.base_price,
        pricing_model: service.pricing_model,
        default_tier: null,
        included_hours: service.pricing_model === 'per_hour' ? 2 : null,
        quantity: 1,
        is_customizable: true,
        option_groups: service.option_groups || [],
      }]
    }));
    setShowServicePicker(false);
    toast.success(`Added ${service.name}`);
  };

  const removeService = (serviceId) => {
    setForm(prev => ({
      ...prev,
      services: prev.services.filter(s => s.service_id !== serviceId)
    }));
  };

  const updateServiceConfig = (serviceId, field, value) => {
    setForm(prev => ({
      ...prev,
      services: prev.services.map(s =>
        s.service_id === serviceId ? { ...s, [field]: value } : s
      )
    }));
  };

  const moveService = (index, direction) => {
    const newServices = [...form.services];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= newServices.length) return;
    [newServices[index], newServices[newIndex]] = [newServices[newIndex], newServices[index]];
    setForm(prev => ({ ...prev, services: newServices }));
  };

  const calculateEstimate = () => {
    let total = 0;
    form.services.forEach(svc => {
      let price = svc.base_price;
      if (svc.default_tier && svc.option_groups) {
        const tierGroup = svc.option_groups.find(g => g.name === 'Package Tier');
        if (tierGroup) {
          const choice = tierGroup.choices?.find(c => c.choice_id === svc.default_tier);
          if (choice) price += choice.price_modifier;
        }
      }
      if (svc.pricing_model === 'per_hour') {
        total += price * (svc.included_hours || 1) * (svc.quantity || 1);
      } else {
        total += price * (svc.quantity || 1);
      }
    });
    if (form.discount_type === 'percentage') {
      total = total * (1 - form.discount_value / 100);
    } else if (form.discount_type === 'fixed') {
      total = Math.max(0, total - form.discount_value);
    }
    return total;
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Package name is required');
      return;
    }
    if (form.services.length === 0) {
      toast.error('Add at least one service');
      return;
    }

    const payload = {
      ...form,
      services: form.services.map(({ option_groups, ...rest }) => rest),
    };

    try {
      if (editingPackage) {
        await axios.put(`${API}/admin/packages/${editingPackage.package_id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Package updated');
      } else {
        await axios.post(`${API}/admin/packages`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Package created');
      }
      fetchPackages();
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save package');
    }
  };

  const filteredServices = allServices.filter(s => {
    if (categoryFilter !== 'all' && s.category !== categoryFilter) return false;
    if (serviceSearch && !s.name.toLowerCase().includes(serviceSearch.toLowerCase())) return false;
    return true;
  });

  const categories = [...new Set(allServices.map(s => s.category))].sort();

  // ── Package List View ──
  if (!showBuilder) {
    return (
      <div>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">All Packages</h2>
          <Button onClick={() => setShowBuilder(true)} data-testid="create-package-btn">
            <Plus className="mr-2 h-4 w-4" /> Create Package
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : packages.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-semibold text-lg mb-2">No packages yet</h3>
              <p className="text-muted-foreground mb-4">
                Create your first bundle by combining food, entertainment, and staffing services.
              </p>
              <Button onClick={() => setShowBuilder(true)}>Create Your First Package</Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {packages.map(pkg => (
              <Card key={pkg.package_id} className={!pkg.is_active ? 'opacity-60' : ''} data-testid={`package-card-${pkg.package_id}`}>
                <CardContent className="flex items-start gap-4 p-4">
                  {pkg.image_url && (
                    <img src={pkg.image_url} alt={pkg.name} className="w-24 h-24 rounded-lg object-cover flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-lg">{pkg.name}</h3>
                      <Badge variant={pkg.is_active ? 'default' : 'secondary'}>
                        {pkg.is_active ? 'Active' : 'Draft'}
                      </Badge>
                      {pkg.is_featured && <Badge className="bg-amber-500">Featured</Badge>}
                    </div>
                    {pkg.short_description && (
                      <p className="text-sm text-muted-foreground mb-2">{pkg.short_description}</p>
                    )}
                    <div className="flex items-center gap-2 flex-wrap">
                      {(pkg.services || []).map(svc => {
                        const color = CATEGORY_COLORS[svc.category] || 'bg-gray-100 text-gray-600';
                        return (
                          <Badge key={svc.service_id} variant="outline" className={`text-xs ${color}`}>
                            {svc.name}
                          </Badge>
                        );
                      })}
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-sm">
                      <span className="text-muted-foreground">
                        {(pkg.services || []).length} services
                      </span>
                      {pkg.discount_type !== 'none' && (
                        <Badge variant="outline" className="bg-green-50 text-green-600 border-green-200">
                          {pkg.discount_type === 'percentage' ? `${pkg.discount_value}% off` : `$${pkg.discount_value} off`}
                        </Badge>
                      )}
                      <span className="text-muted-foreground">
                        {pkg.min_guests}-{pkg.max_guests} guests
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button variant="outline" size="sm" onClick={() => setPreviewPackage(pkg)} data-testid={`preview-package-${pkg.package_id}`}>
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleEdit(pkg)} data-testid={`edit-package-${pkg.package_id}`}>
                      Edit
                    </Button>
                    <Button variant="destructive" size="sm" onClick={() => handleDelete(pkg.package_id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Preview Dialog */}
        {previewPackage && (
          <Dialog open={!!previewPackage} onOpenChange={() => setPreviewPackage(null)}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{previewPackage.name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                {previewPackage.image_url && (
                  <img src={previewPackage.image_url} alt={previewPackage.name} className="w-full h-48 object-cover rounded-lg" />
                )}
                <p className="text-muted-foreground">{previewPackage.description}</p>
                <div>
                  <h4 className="font-semibold mb-2">Included Services</h4>
                  <div className="space-y-2">
                    {(previewPackage.services || []).map(svc => (
                      <div key={svc.service_id} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                        {svc.image_url && <img src={svc.image_url} alt={svc.name} className="w-12 h-12 rounded object-cover" />}
                        <div className="flex-1">
                          <p className="font-medium">{svc.name}</p>
                          <p className="text-xs text-muted-foreground">
                            ${svc.base_price}{PRICING_LABELS[svc.pricing_model] || ''}
                            {svc.quantity > 1 && ` x ${svc.quantity}`}
                            {svc.included_hours && ` (${svc.included_hours}hrs)`}
                          </p>
                        </div>
                        {svc.is_customizable && <Badge variant="outline" className="text-xs">Customizable</Badge>}
                      </div>
                    ))}
                  </div>
                </div>
                {previewPackage.discount_type !== 'none' && (
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 text-green-700 dark:text-green-400 text-sm">
                    Package discount: {previewPackage.discount_type === 'percentage' ? `${previewPackage.discount_value}% off` : `$${previewPackage.discount_value} off`}
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    );
  }

  // ── Package Builder View ──
  return (
    <div>
      <Button variant="ghost" className="mb-4" onClick={resetForm}>
        <ArrowLeft className="mr-2 h-4 w-4" /> Back to Packages
      </Button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Package Details */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{editingPackage ? 'Edit Package' : 'Create Package'}</CardTitle>
              <CardDescription>Bundle services together into a single package for customers</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Package Name *</Label>
                  <Input
                    value={form.name}
                    onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., The Denver Party Package"
                    data-testid="package-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cover Image URL</Label>
                  <Input
                    value={form.image_url}
                    onChange={e => setForm(prev => ({ ...prev, image_url: e.target.value }))}
                    placeholder="https://..."
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Short Description</Label>
                <Input
                  value={form.short_description}
                  onChange={e => setForm(prev => ({ ...prev, short_description: e.target.value }))}
                  placeholder="One-line description for cards"
                  data-testid="package-short-desc-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Full Description</Label>
                <Textarea
                  value={form.description}
                  onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  placeholder="Detailed description of what this package offers..."
                />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Min Guests</Label>
                  <Input type="number" value={form.min_guests} onChange={e => setForm(prev => ({ ...prev, min_guests: parseInt(e.target.value) || 0 }))} />
                </div>
                <div className="space-y-2">
                  <Label>Max Guests</Label>
                  <Input type="number" value={form.max_guests} onChange={e => setForm(prev => ({ ...prev, max_guests: parseInt(e.target.value) || 0 }))} />
                </div>
                <div className="space-y-2">
                  <Label>Active</Label>
                  <div className="pt-2">
                    <Switch checked={form.is_active} onCheckedChange={v => setForm(prev => ({ ...prev, is_active: v }))} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Featured</Label>
                  <div className="pt-2">
                    <Switch checked={form.is_featured} onCheckedChange={v => setForm(prev => ({ ...prev, is_featured: v }))} />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Services in Package */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Services in Package</CardTitle>
                  <CardDescription>{form.services.length} service{form.services.length !== 1 && 's'} added</CardDescription>
                </div>
                <Button onClick={() => setShowServicePicker(true)} data-testid="add-service-to-package-btn">
                  <Plus className="mr-2 h-4 w-4" /> Add Service
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {form.services.length === 0 ? (
                <div className="text-center py-8 border-2 border-dashed rounded-lg">
                  <Package className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground mb-2">No services added yet</p>
                  <Button variant="outline" onClick={() => setShowServicePicker(true)}>
                    Browse Services
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {form.services.map((svc, index) => {
                    const catColor = CATEGORY_COLORS[svc.category] || '';
                    const tierGroup = svc.option_groups?.find(g => g.name === 'Package Tier');

                    return (
                      <div key={svc.service_id} className="border rounded-lg p-4" data-testid={`package-service-${svc.service_id}`}>
                        <div className="flex items-start gap-3">
                          {/* Reorder */}
                          <div className="flex flex-col gap-1 pt-1">
                            <button onClick={() => moveService(index, -1)} disabled={index === 0}
                              className="text-muted-foreground hover:text-foreground disabled:opacity-30">
                              <ChevronUp className="h-4 w-4" />
                            </button>
                            <GripVertical className="h-4 w-4 text-muted-foreground" />
                            <button onClick={() => moveService(index, 1)} disabled={index === form.services.length - 1}
                              className="text-muted-foreground hover:text-foreground disabled:opacity-30">
                              <ChevronDown className="h-4 w-4" />
                            </button>
                          </div>

                          {/* Service image */}
                          {svc.image_url && (
                            <img src={svc.image_url} alt={svc.name} className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                          )}

                          {/* Service details */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold">{svc.name}</h4>
                              <Badge variant="outline" className={`text-xs ${catColor}`}>{svc.category}</Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              ${svc.base_price}{PRICING_LABELS[svc.pricing_model] || ''}
                            </p>

                            {/* Configuration */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                              {/* Tier selector */}
                              {tierGroup && tierGroup.choices?.length > 0 && (
                                <div className="space-y-1">
                                  <Label className="text-xs">Default Tier</Label>
                                  <Select
                                    value={svc.default_tier || ''}
                                    onValueChange={v => updateServiceConfig(svc.service_id, 'default_tier', v || null)}
                                  >
                                    <SelectTrigger className="h-8 text-xs">
                                      <SelectValue placeholder="Basic" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {tierGroup.choices.map(ch => (
                                        <SelectItem key={ch.choice_id} value={ch.choice_id}>
                                          {ch.name} {ch.price_modifier > 0 && `(+$${ch.price_modifier})`}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              )}

                              {/* Quantity */}
                              <div className="space-y-1">
                                <Label className="text-xs">Qty</Label>
                                <Input
                                  type="number" min="1" max="20"
                                  value={svc.quantity || 1}
                                  onChange={e => updateServiceConfig(svc.service_id, 'quantity', parseInt(e.target.value) || 1)}
                                  className="h-8 text-xs"
                                />
                              </div>

                              {/* Hours (for per_hour services) */}
                              {svc.pricing_model === 'per_hour' && (
                                <div className="space-y-1">
                                  <Label className="text-xs">Hours</Label>
                                  <Input
                                    type="number" min="1" max="12"
                                    value={svc.included_hours || 2}
                                    onChange={e => updateServiceConfig(svc.service_id, 'included_hours', parseInt(e.target.value) || 1)}
                                    className="h-8 text-xs"
                                  />
                                </div>
                              )}

                              {/* Customizable toggle */}
                              <div className="space-y-1">
                                <Label className="text-xs">Customizable</Label>
                                <div className="pt-1">
                                  <Switch
                                    checked={svc.is_customizable}
                                    onCheckedChange={v => updateServiceConfig(svc.service_id, 'is_customizable', v)}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Remove */}
                          <Button variant="ghost" size="sm" onClick={() => removeService(svc.service_id)}
                            className="text-destructive hover:text-destructive flex-shrink-0">
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right: Pricing & Summary */}
        <div className="space-y-6">
          <Card className="sticky top-24">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Settings2 className="h-5 w-5" /> Pricing & Discount
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Discount Type</Label>
                <Select value={form.discount_type} onValueChange={v => setForm(prev => ({ ...prev, discount_type: v }))}>
                  <SelectTrigger data-testid="discount-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Discount</SelectItem>
                    <SelectItem value="percentage">Percentage Off</SelectItem>
                    <SelectItem value="fixed">Fixed Amount Off</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {form.discount_type !== 'none' && (
                <div className="space-y-2">
                  <Label>{form.discount_type === 'percentage' ? 'Discount %' : 'Discount Amount ($)'}</Label>
                  <div className="relative">
                    {form.discount_type === 'percentage' ? (
                      <Percent className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    ) : (
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    )}
                    <Input
                      type="number" min="0"
                      value={form.discount_value}
                      onChange={e => setForm(prev => ({ ...prev, discount_value: parseFloat(e.target.value) || 0 }))}
                      className="pl-9"
                      data-testid="discount-value-input"
                    />
                  </div>
                </div>
              )}

              {/* Summary */}
              <div className="border-t pt-4 mt-4">
                <h4 className="font-semibold mb-3">Package Summary</h4>
                <div className="space-y-2 text-sm">
                  {form.services.map(svc => {
                    let linePrice = svc.base_price;
                    if (svc.default_tier && svc.option_groups) {
                      const tg = svc.option_groups.find(g => g.name === 'Package Tier');
                      const ch = tg?.choices?.find(c => c.choice_id === svc.default_tier);
                      if (ch) linePrice += ch.price_modifier;
                    }
                    const unit = svc.pricing_model === 'per_hour' ? `${svc.included_hours || 1}hrs` : `x${svc.quantity || 1}`;
                    const total = svc.pricing_model === 'per_hour'
                      ? linePrice * (svc.included_hours || 1) * (svc.quantity || 1)
                      : linePrice * (svc.quantity || 1);

                    return (
                      <div key={svc.service_id} className="flex justify-between">
                        <span className="text-muted-foreground truncate mr-2">
                          {svc.name} ({unit})
                        </span>
                        <span className="font-medium whitespace-nowrap">
                          ${total.toFixed(2)}{svc.pricing_model === 'per_person' ? '/pp' : ''}
                        </span>
                      </div>
                    );
                  })}

                  {form.services.length > 0 && (
                    <>
                      <div className="border-t pt-2 mt-2">
                        <div className="flex justify-between font-medium">
                          <span>Subtotal</span>
                          <span>${calculateEstimate().toFixed(2)}{form.services.some(s => s.pricing_model === 'per_person') ? '/pp*' : ''}</span>
                        </div>
                        {form.discount_type !== 'none' && (
                          <div className="flex justify-between text-green-600 text-xs mt-1">
                            <span>Discount</span>
                            <span>-{form.discount_type === 'percentage' ? `${form.discount_value}%` : `$${form.discount_value}`}</span>
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        * Per-person services are estimated at base tier. Customers can customize each service.
                      </p>
                    </>
                  )}
                </div>
              </div>

              <Button className="w-full" onClick={handleSave} disabled={form.services.length === 0} data-testid="save-package-btn">
                <Save className="mr-2 h-4 w-4" />
                {editingPackage ? 'Update Package' : 'Create Package'}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Service Picker Dialog */}
      <Dialog open={showServicePicker} onOpenChange={setShowServicePicker}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Add Service to Package</DialogTitle>
          </DialogHeader>

          <div className="flex items-center gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={serviceSearch}
                onChange={e => setServiceSearch(e.target.value)}
                placeholder="Search services..."
                className="pl-9"
                data-testid="service-search-input"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="overflow-y-auto max-h-[50vh] space-y-2">
            {filteredServices.map(svc => {
              const alreadyAdded = form.services.some(s => s.service_id === svc.service_id);
              const CatIcon = CATEGORY_ICONS[svc.category] || Package;
              const catColor = CATEGORY_COLORS[svc.category] || '';

              return (
                <div
                  key={svc.service_id}
                  className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${alreadyAdded ? 'opacity-50 bg-muted' : 'hover:bg-muted/50 cursor-pointer'}`}
                  onClick={() => !alreadyAdded && addService(svc)}
                  data-testid={`picker-service-${svc.service_id}`}
                >
                  {svc.image_url ? (
                    <img src={svc.image_url} alt={svc.name} className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-14 h-14 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                      <CatIcon className="h-6 w-6 text-muted-foreground" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{svc.name}</p>
                      <Badge variant="outline" className={`text-xs ${catColor}`}>{svc.category}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground truncate">{svc.short_description}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="font-semibold">${svc.base_price}</p>
                    <p className="text-xs text-muted-foreground">{PRICING_LABELS[svc.pricing_model] || ''}</p>
                  </div>
                  {alreadyAdded ? (
                    <Badge variant="secondary" className="text-xs">Added</Badge>
                  ) : (
                    <Plus className="h-5 w-5 text-indigo-500 flex-shrink-0" />
                  )}
                </div>
              );
            })}
            {filteredServices.length === 0 && (
              <p className="text-center text-muted-foreground py-8">No services found</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
